import React, { useState, useEffect } from "react";
import {
	Box,
	Button,
	Container,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	CircularProgress,
	Alert,
	Snackbar,
	Grid,
	Card,
	CardContent,
	Chip,
	Stack,
	Divider,
	Autocomplete,
	Checkbox,
	IconButton,
	Tooltip,
} from "@mui/material";
import {
	Download as DownloadIcon,
	Print as PrintIcon,
	Refresh as RefreshIcon,
	CheckBox as CheckBoxIcon,
	CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
	Group as GroupIcon,
	Delete as DeleteIcon,
} from "@mui/icons-material";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useSelectedAcademicYear } from "../../hooks/useSelectedAcademicYear";
import AcademicYearSelector from "../../components/common/AcademicYearSelector";
import { monthYearInAcademicYear, filterReportSettingsForCumulative } from "../../utils/academicYear";
import {
	applyReportModifications,
	getSourceIndicesForDisplayRow,
	indicesToModifications,
	loadReportModifications,
	mapModificationsToIndices,
	saveReportModifications,
} from "../../utils/reportModifications";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';

const ReportGeneration = () => {
	const { selectedAcademicYear, setSelectedAcademicYear } =
		useSelectedAcademicYear();

	useEffect(() => {
		setSelectedPeriod("");
		setReportData(null);
		setModifiedReportData(null);
		setSelectedRows(new Set());
		setExcludedRows(new Set());
		setGroupedRows({});
	}, [selectedAcademicYear]);

	const [schools, setSchools] = useState([]);
	const [selectedSchool, setSelectedSchool] = useState("");
	const [reportData, setReportData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [generating, setGenerating] = useState(false);
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
	const [reportSettings, setReportSettings] = useState([]);
	const [selectedPeriod, setSelectedPeriod] = useState("");
	const { user } = useAuth();
	
	// State for row modifications
	const [selectedRows, setSelectedRows] = useState(new Set());
	const [excludedRows, setExcludedRows] = useState(new Set());
	const [groupedRows, setGroupedRows] = useState({}); // Map of groupId -> array of row indices
	const [modifiedReportData, setModifiedReportData] = useState(null);
	const [nextGroupId, setNextGroupId] = useState(1);

	useEffect(() => {
		fetchSchools();
		fetchReportSettings();
	}, []);

	const fetchSchools = async () => {
		try {
			// Check user role
			if (!user?.profile?.role) {
				showSnackbar("User profile not found", "error");
				return;
			}

			// If user is ROLE_FUNMIYO_ADMIN, show all schools
			if (user.profile.role === "ROLE_FUNMIYO_ADMIN") {
				const { data, error } = await supabase
					.from("schools")
					.select("id, name")
					.order("name");

				if (error) throw error;
				setSchools(data || []);
			} else {
				// For non-admin users, fetch only their associated schools
				const { data, error } = await supabase
					.from("employee_schools")
					.select(`
						school_id,
						schools:school_id (
							id,
							name
						)
					`)
					.eq("profile_id", user.id)
					.eq("active_status", true);

				if (error) throw error;

				// Extract school data from the joined result
				const associatedSchools = data
					?.filter(item => item.schools && item.schools.id)
					?.map(item => ({
						id: item.schools.id,
						name: item.schools.name
					})) || [];

				setSchools(associatedSchools);
			}
		} catch (error) {
			console.error("Error fetching schools:", error);
			showSnackbar("Error fetching schools", "error");
		}
	};

	const fetchReportSettings = async () => {
		try {
			const { data, error } = await supabase
				.from("report_settings")
				.select("*")
				.eq("status", true)
				.order("year", { ascending: false })
				.order("month", { ascending: false });

			if (error) throw error;
			setReportSettings(data || []);
			
			// No default selection needed since we removed grade settings
		} catch (error) {
			console.error("Error fetching report settings:", error);
			showSnackbar("Error fetching report settings", "error");
		}
	};

	const showSnackbar = (message, severity = "success") => {
		setSnackbar({ open: true, message, severity });
	};

	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

	const generateReport = async () => {
		if (!selectedSchool || !selectedPeriod) {
			showSnackbar("Please select a school and report period", "error");
			return;
		}

		setGenerating(true);
		try {
			const [month, year] = selectedPeriod.split('/').map(Number);
			const settingsForPeriod = reportSettings.filter(s => s.month === month && s.year === year);
			
			if (settingsForPeriod.length === 0) {
				showSnackbar("No settings found for the selected period", "error");
				return;
			}

			// Fetch school details
			const { data: school, error: schoolError } = await supabase
				.from("schools")
				.select("id, name")
				.eq("id", selectedSchool)
				.single();

			if (schoolError) throw schoolError;

			// Fetch teachers for this school
			const { data: teachers, error: teachersError } = await supabase
				.from("profiles")
				.select(`
					id,
					full_name,
					school_id
				`)
				.eq("school_id", selectedSchool)
				.eq("role", "ROLE_SCHOOL_TEACHER");

			if (teachersError) throw teachersError;

			// Fetch grades for this school
			const { data: schoolGrades, error: gradesError } = await supabase
				.from("school_grades")
				.select(`
					grade_id,
					grades:grade_id (
						id,
						name,
						parent_grade
					)
				`)
				.eq("school_id", selectedSchool);

			if (gradesError) throw gradesError;

			// Fetch teacher-grade assignments
			const teacherIds = teachers.map(t => t.id);
			const { data: teacherGrades, error: teacherGradesError } = await supabase
				.from("teacher_grades")
				.select(`
					profile_id,
					grade_id,
					grades:grade_id (
						id,
						name
					)
				`)
				.in("profile_id", teacherIds)
				.eq("school_id", selectedSchool);

			if (teacherGradesError) throw teacherGradesError;

			// Fetch all game results for these teachers (for cumulative calculation)
			const { data: allGameResults, error: allGameResultsError } = await supabase
				.from("game_results")
				.select(`
					id,
					teacher_id,
					category_id,
					content_id,
					grade_id,
					created_at,
					course_categories:category_id (
						name,
						concept
					),
					course_contents:content_id (
						title,
						content_type
					)
				`)
				.in("teacher_id", teacherIds)
				.eq("academic_year", selectedAcademicYear);

			if (allGameResultsError) throw allGameResultsError;

			// Filter game results for monthly data (selected month only)
			const startDate = new Date(year, month - 1, 1);
			const endDate = new Date(year, month, 0, 23, 59, 59);
			
			const monthlyGameResults = allGameResults ? allGameResults.filter(gr => {
				const createdDate = new Date(gr.created_at);
				return createdDate >= startDate && createdDate <= endDate;
			}) : [];


			// Process the data to match the report format for all grades
			const processedData = await processReportDataForAllGrades(
				school,
				teachers,
				schoolGrades,
				teacherGrades,
				allGameResults,
				monthlyGameResults,
				settingsForPeriod,
				reportSettings,
				selectedAcademicYear
			);

			const persistedMods = loadReportModifications(
				selectedSchool,
				selectedAcademicYear
			);
			const {
				excludedRows: loadedExcluded,
				groupedRows: loadedGrouped,
				nextGroupId: loadedNextGroupId,
			} = mapModificationsToIndices(
				processedData.reportRows.monthly,
				persistedMods
			);

			setReportData(processedData);
			setSelectedRows(new Set());
			setExcludedRows(loadedExcluded);
			setGroupedRows(loadedGrouped);
			setNextGroupId(loadedNextGroupId);

			if (
				loadedExcluded.size > 0 ||
				Object.keys(loadedGrouped).length > 0
			) {
				setModifiedReportData(
					applyReportModifications(
						processedData,
						loadedExcluded,
						loadedGrouped
					)
				);
			} else {
				setModifiedReportData(null);
			}
			showSnackbar("Report generated successfully", "success");
		} catch (error) {
			console.error("Error generating report:", error);
			showSnackbar("Error generating report", "error");
		} finally {
			setGenerating(false);
		}
	};

	const processReportDataForAllGrades = async (school, teachers, schoolGrades, teacherGrades, allGameResults, monthlyGameResults, settingsForPeriod, reportSettings, academicYear) => {
		// Use the first setting as the primary setting for month/year reference
		const primarySetting = settingsForPeriod[0];
		
		// Active months within the academic year, up to and including the selected month
		const getAllActiveSettings = () => {
			const filteredSettings = filterReportSettingsForCumulative(
				reportSettings,
				primarySetting.month,
				primarySetting.year,
				academicYear
			);

			return filteredSettings.sort((a, b) => {
				if (a.year !== b.year) return a.year - b.year;
				return a.month - b.month;
			});
		};

		const allActiveSettings = getAllActiveSettings();
		
		const monthlyContentByTeacher = {};
		const cumulativeContentByTeacher = {};
		
		// Find last activity date for each teacher (to determine when they became inactive)
		const lastActivityByTeacher = {};
		if (allGameResults && allGameResults.length > 0) {
			allGameResults.forEach(gr => {
				if (gr.teacher_id && gr.created_at) {
					const teacherId = gr.teacher_id;
					const activityDate = new Date(gr.created_at);
					
					if (!lastActivityByTeacher[teacherId] || activityDate > lastActivityByTeacher[teacherId]) {
						lastActivityByTeacher[teacherId] = activityDate;
					}
				}
			});
		}
		
		// Process game results for monthly data (selected month only)
		// Deduplicate by content_id AND grade_id per teacher to avoid counting the same content multiple times
		if (monthlyGameResults && monthlyGameResults.length > 0) {
			// Use a Map to track unique content_ids per teacher AND grade for monthly data
			// Key: teacherId, Value: Map of gradeId -> Set of contentIds
			const uniqueMonthlyContentIdsByTeacherAndGrade = {};
			
			monthlyGameResults.forEach(gr => {
				if (gr.content_id && gr.teacher_id && gr.grade_id) {
					const teacherId = gr.teacher_id;
					const gradeId = gr.grade_id;
					const contentId = gr.content_id;
					
					// Initialize structure for this teacher if it doesn't exist
					if (!uniqueMonthlyContentIdsByTeacherAndGrade[teacherId]) {
						uniqueMonthlyContentIdsByTeacherAndGrade[teacherId] = {};
					}
					
					// Initialize Set for this teacher-grade combination if it doesn't exist
					if (!uniqueMonthlyContentIdsByTeacherAndGrade[teacherId][gradeId]) {
						uniqueMonthlyContentIdsByTeacherAndGrade[teacherId][gradeId] = new Set();
					}
					
					// Only add each content_id once per teacher-grade combination
					if (!uniqueMonthlyContentIdsByTeacherAndGrade[teacherId][gradeId].has(contentId)) {
						uniqueMonthlyContentIdsByTeacherAndGrade[teacherId][gradeId].add(contentId);
						
						// Initialize counter for this teacher-grade combination if needed
						const key = `${teacherId}_${gradeId}`;
						if (!monthlyContentByTeacher[key]) {
							monthlyContentByTeacher[key] = { preGame: 0, gameRules: 0, total: 0 };
						}

						const contentType = gr.course_contents?.content_type || '';
						const categoryConcept = gr.course_categories?.concept || '';
						const contentTitle = gr.course_contents?.title || '';

						// Prioritize content_title for more specific categorization
						// Check Pre-Game first to avoid "Pre-Game Activity" being categorized as Game Play
						if (contentTitle.toLowerCase().includes('pre') || 
							contentTitle.toLowerCase().includes('introduction')) {
							monthlyContentByTeacher[key].preGame++;
						} else if (contentTitle.toLowerCase().includes('game') || 
								   contentTitle.toLowerCase().includes('rule')) {
							monthlyContentByTeacher[key].gameRules++;
						} else if (contentType.toLowerCase().includes('pre') || 
								   contentType.toLowerCase().includes('introduction') ||
								   categoryConcept.toLowerCase().includes('pre') ||
								   categoryConcept.toLowerCase().includes('introduction')) {
							monthlyContentByTeacher[key].preGame++;
						} else if (contentType.toLowerCase().includes('game') || 
								   contentType.toLowerCase().includes('rule') ||
								   categoryConcept.toLowerCase().includes('game') ||
								   categoryConcept.toLowerCase().includes('rule')) {
							monthlyContentByTeacher[key].gameRules++;
						} else {
							monthlyContentByTeacher[key].preGame++; // Default
						}
						monthlyContentByTeacher[key].total++;
					}
				}
			});
		}

		// Process cumulative data for all active months using game_results
		// Deduplicate by content_id AND grade_id per teacher to avoid counting the same content multiple times
		if (allGameResults && allGameResults.length > 0) {
			// Use a Map to track unique content_ids per teacher AND grade
			// Key: teacherId, Value: Map of gradeId -> Set of contentIds
			const uniqueContentIdsByTeacherAndGrade = {};
			
			allGameResults.forEach(gr => {
				const createdDate = new Date(gr.created_at);
				const createdMonth = createdDate.getMonth() + 1; // JavaScript months are 0-based
				const createdYear = createdDate.getFullYear();
				
				// Check if this game result is within any of the active months
				const isInActivePeriod = allActiveSettings.some(setting => 
					setting.month === createdMonth && setting.year === createdYear
				);
				
				if (isInActivePeriod && gr.content_id && gr.teacher_id && gr.grade_id) {
					const teacherId = gr.teacher_id;
					const gradeId = gr.grade_id;
					const contentId = gr.content_id;
					
					// Initialize structure for this teacher if it doesn't exist
					if (!uniqueContentIdsByTeacherAndGrade[teacherId]) {
						uniqueContentIdsByTeacherAndGrade[teacherId] = {};
					}
					
					// Initialize Set for this teacher-grade combination if it doesn't exist
					if (!uniqueContentIdsByTeacherAndGrade[teacherId][gradeId]) {
						uniqueContentIdsByTeacherAndGrade[teacherId][gradeId] = new Set();
					}
					
					// Only add each content_id once per teacher-grade combination
					if (!uniqueContentIdsByTeacherAndGrade[teacherId][gradeId].has(contentId)) {
						uniqueContentIdsByTeacherAndGrade[teacherId][gradeId].add(contentId);
						
						// Initialize counter for this teacher-grade combination if needed
						const key = `${teacherId}_${gradeId}`;
						if (!cumulativeContentByTeacher[key]) {
							cumulativeContentByTeacher[key] = { preGame: 0, gameRules: 0, total: 0 };
						}
						
						// Categorize and count this unique content item
						const contentType = gr.course_contents?.content_type || '';
						const categoryConcept = gr.course_categories?.concept || '';
						const contentTitle = gr.course_contents?.title || '';

						// Prioritize content_title for more specific categorization
						// Check Pre-Game first to avoid "Pre-Game Activity" being categorized as Game Play
						if (contentTitle.toLowerCase().includes('pre') || 
							contentTitle.toLowerCase().includes('introduction')) {
							cumulativeContentByTeacher[key].preGame++;
						} else if (contentTitle.toLowerCase().includes('game') || 
								   contentTitle.toLowerCase().includes('rule')) {
							cumulativeContentByTeacher[key].gameRules++;
						} else if (contentType.toLowerCase().includes('pre') || 
								   contentType.toLowerCase().includes('introduction') ||
								   categoryConcept.toLowerCase().includes('pre') ||
								   categoryConcept.toLowerCase().includes('introduction')) {
							cumulativeContentByTeacher[key].preGame++;
						} else if (contentType.toLowerCase().includes('game') || 
								   contentType.toLowerCase().includes('rule') ||
								   categoryConcept.toLowerCase().includes('game') ||
								   categoryConcept.toLowerCase().includes('rule')) {
							cumulativeContentByTeacher[key].gameRules++;
						} else {
							cumulativeContentByTeacher[key].preGame++; // Default
						}
						cumulativeContentByTeacher[key].total++;
					}
				}
			});
		}


		// Helper function to get videos required for a specific grade
		const getVideosRequiredForGrade = (gradeName, setting) => {
			// Find the grade in schoolGrades to get its parent_grade
			const schoolGrade = schoolGrades.find(sg => sg.grades.name === gradeName);
			const gradeParentGrade = schoolGrade ? schoolGrade.grades.parent_grade : null;
			
			
			const matchingSetting = reportSettings.find(s => 
				s.parent_grade === gradeParentGrade && 
				s.month === setting.month && 
				s.year === setting.year
			);
			
			const result = matchingSetting ? matchingSetting.number_of_videos_to_be_completed : setting.number_of_videos_to_be_completed;
			
			return result;
		};

		// Helper function to get cumulative videos required for a specific grade
		const getCumulativeVideosRequiredForGrade = (gradeName) => {
			// Find the grade in schoolGrades to get its parent_grade
			const schoolGrade = schoolGrades.find(sg => sg.grades.name === gradeName);
			const gradeParentGrade = schoolGrade ? schoolGrade.grades.parent_grade : null;
			
			// Filter settings for this specific grade by parent_grade
			const gradeSettings = allActiveSettings.filter(setting => setting.parent_grade === gradeParentGrade);
			
			// Sum up videos required from all active settings for this grade
			const cumulativeVideos = gradeSettings.reduce((sum, setting) => {
				return sum + setting.number_of_videos_to_be_completed;
			}, 0);
			
			return cumulativeVideos;
		};

		// Process each teacher and generate report rows for all grades
		const reportRows = {
			monthly: [],
			cumulative: []
		};

		teachers.forEach(teacher => {
			const teacherGradeAssignments = teacherGrades.filter(tg => tg.profile_id === teacher.id);
			
			if (teacherGradeAssignments.length > 0) {
				// Teacher has specific grade assignments
				teacherGradeAssignments.forEach(teacherGrade => {
					const gradeName = teacherGrade.grades.name;
					const gradeId = teacherGrade.grade_id;
					
					// Get content counts for this specific teacher-grade combination
					const monthlyKey = `${teacher.id}_${gradeId}`;
					const cumulativeKey = `${teacher.id}_${gradeId}`;
					
					const teacherMonthlyContent = monthlyContentByTeacher[monthlyKey] || { preGame: 0, gameRules: 0, total: 0 };
					const teacherCumulativeContent = cumulativeContentByTeacher[cumulativeKey] || { preGame: 0, gameRules: 0, total: 0 };
					
					const videosRequired = getVideosRequiredForGrade(gradeName, primarySetting);
					
					// Calculate implementation score for this month based on completed content only
					const monthlyScore = teacherMonthlyContent.total > 0 
						? Math.min(100, (teacherMonthlyContent.total / videosRequired) * 100)
						: 0;

					// Calculate cumulative score based on cumulative content
					const cumulativeVideosRequired = getCumulativeVideosRequiredForGrade(gradeName);
					const cumulativeScore = teacherCumulativeContent.total > 0 
						? Math.min(100, (teacherCumulativeContent.total / cumulativeVideosRequired) * 100)
						: 0;

					// Monthly row
					reportRows.monthly.push({
						schoolName: school.name,
						gradeName: gradeName,
						teacherName: teacher.full_name,
						preGameActivity: teacherMonthlyContent.preGame,
						gameRules: teacherMonthlyContent.gameRules,
						videosRequired: videosRequired,
						implementationScore: monthlyScore
					});

					// Cumulative row
					reportRows.cumulative.push({
						schoolName: school.name,
						gradeName: gradeName,
						teacherName: teacher.full_name,
						preGameActivity: teacherCumulativeContent.preGame,
						gameRules: teacherCumulativeContent.gameRules,
						videosRequired: cumulativeVideosRequired,
						implementationScore: cumulativeScore
					});
				});
			} else {
				// Teacher has no grade assignments - create one row per grade from settings
				// Get unique parent grades from settings
				const uniqueParentGrades = [...new Set(settingsForPeriod.map(s => s.parent_grade))];
				
				uniqueParentGrades.forEach(parentGrade => {
					// Find the first actual grade name from schoolGrades that matches this parent_grade
					const matchingSchoolGrade = schoolGrades.find(sg => sg.grades.parent_grade === parentGrade);
					const actualGradeName = matchingSchoolGrade ? matchingSchoolGrade.grades.name : parentGrade;
					const gradeId = matchingSchoolGrade ? matchingSchoolGrade.grade_id : null;
					
					// Get the setting for this parent_grade (use the first one found)
					const setting = settingsForPeriod.find(s => s.parent_grade === parentGrade);
					if (!setting) return;
					
					// Get content counts for this specific teacher-grade combination
					// If gradeId is not available, try to find it from schoolGrades or use a fallback
					let monthlyKey, cumulativeKey;
					if (gradeId) {
						monthlyKey = `${teacher.id}_${gradeId}`;
						cumulativeKey = `${teacher.id}_${gradeId}`;
					} else {
						// Fallback: use teacher-level counts if grade_id is not available
						monthlyKey = teacher.id;
						cumulativeKey = teacher.id;
					}
					
					const teacherMonthlyContent = monthlyContentByTeacher[monthlyKey] || { preGame: 0, gameRules: 0, total: 0 };
					const teacherCumulativeContent = cumulativeContentByTeacher[cumulativeKey] || { preGame: 0, gameRules: 0, total: 0 };
					
					const videosRequired = setting.number_of_videos_to_be_completed;
					
					// Calculate implementation score for this month based on completed content only
					const monthlyScore = teacherMonthlyContent.total > 0 
						? Math.min(100, (teacherMonthlyContent.total / videosRequired) * 100)
						: 0;

					// Calculate cumulative score based on cumulative content
					const cumulativeVideosRequired = getCumulativeVideosRequiredForGrade(actualGradeName);
					const cumulativeScore = teacherCumulativeContent.total > 0 
						? Math.min(100, (teacherCumulativeContent.total / cumulativeVideosRequired) * 100)
						: 0;

					// Monthly row
					reportRows.monthly.push({
						schoolName: school.name,
						gradeName: actualGradeName,
						teacherName: teacher.full_name,
						preGameActivity: teacherMonthlyContent.preGame,
						gameRules: teacherMonthlyContent.gameRules,
						videosRequired: videosRequired,
						implementationScore: monthlyScore
					});

					// Cumulative row
					reportRows.cumulative.push({
						schoolName: school.name,
						gradeName: actualGradeName,
						teacherName: teacher.full_name,
						preGameActivity: teacherCumulativeContent.preGame,
						gameRules: teacherCumulativeContent.gameRules,
						videosRequired: cumulativeVideosRequired,
						implementationScore: cumulativeScore
					});
				});
			}
		});


		// Calculate totals
		const totalTeachers = reportRows.monthly.length;
		const totalPreGameActivity = reportRows.monthly.reduce((sum, row) => sum + row.preGameActivity, 0);
		const totalGameRules = reportRows.monthly.reduce((sum, row) => sum + row.gameRules, 0);
		const totalVideosRequired = reportRows.monthly.reduce((sum, row) => sum + row.videosRequired, 0);
		
		
		// Calculate current month average from monthly implementation scores
		// Exclude 0% scores only if teacher was already inactive during this month
		const monthlyScores = reportRows.monthly
			.map(row => {
				// Find the teacher ID for this row
				const teacher = teachers.find(t => t.full_name === row.teacherName);
				if (!teacher) return { score: row.implementationScore, include: true };
				
				const teacherId = teacher.id;
				const lastActivity = lastActivityByTeacher[teacherId];
				const reportMonth = new Date(primarySetting.year, primarySetting.month - 1, 1);
				
				// If teacher has 0% score, check if they were inactive during this month
				if (row.implementationScore === 0) {
					// If teacher never had activity OR last activity was before this month, exclude from average
					if (!lastActivity || lastActivity < reportMonth) {
						return { score: row.implementationScore, include: false };
					}
				}
				
				return { score: row.implementationScore, include: true };
			})
			.filter(item => !isNaN(item.score));
		
		const includedMonthlyScores = monthlyScores.filter(item => item.include).map(item => item.score);
		const avgImplementationScore = includedMonthlyScores.length > 0 
			? includedMonthlyScores.reduce((sum, score) => sum + score, 0) / includedMonthlyScores.length 
			: 0;
			

		// Calculate cumulative average from cumulative implementation scores
		// Exclude 0% scores only if teacher was already inactive during the cumulative period
		const cumulativeScores = reportRows.cumulative
			.map(row => {
				// Find the teacher ID for this row
				const teacher = teachers.find(t => t.full_name === row.teacherName);
				if (!teacher) return { score: row.implementationScore, include: true };
				
				const teacherId = teacher.id;
				const lastActivity = lastActivityByTeacher[teacherId];
				
				// Get the earliest and latest months in the cumulative period
				const earliestActiveMonth = allActiveSettings.length > 0 
					? new Date(allActiveSettings[0].year, allActiveSettings[0].month - 1, 1)
					: null;
				const latestActiveMonth = allActiveSettings.length > 0 
					? new Date(allActiveSettings[allActiveSettings.length - 1].year, allActiveSettings[allActiveSettings.length - 1].month, 0, 23, 59, 59)
					: null;
				
				// If teacher has 0% score, check if they were inactive during the cumulative period
				if (row.implementationScore === 0) {
					// If teacher never had activity, exclude from average
					if (!lastActivity) {
						return { score: row.implementationScore, include: false };
					}
					
					// If last activity was before the cumulative period started, exclude from average
					if (earliestActiveMonth && lastActivity < earliestActiveMonth) {
						return { score: row.implementationScore, include: false };
					}
					
					// If last activity was during the cumulative period, include (they were active but didn't complete)
					// This handles the case where they had activity in an earlier month of the period
				}
				
				return { score: row.implementationScore, include: true };
			})
			.filter(item => !isNaN(item.score));
		
		const includedCumulativeScores = cumulativeScores.filter(item => item.include).map(item => item.score);
		const avgCumulativeScore = includedCumulativeScores.length > 0 
			? includedCumulativeScores.reduce((sum, score) => sum + score, 0) / includedCumulativeScores.length 
			: 0;
			

		// Calculate school score and category
		const schoolScore = avgCumulativeScore;
		let schoolCategory;
		if (schoolScore >= 90) {
			schoolCategory = { category: "Excellent", color: "success" };
		} else if (schoolScore >= 80) {
			schoolCategory = { category: "Good", color: "info" };
		} else if (schoolScore >= 60) {
			schoolCategory = { category: "Average", color: "warning" };
		} else if (schoolScore >= 50) {
			schoolCategory = { category: "Below Average", color: "warning" };
		} else {
			schoolCategory = { category: "Poor", color: "error" };
		}

		// Cumulative period label — academic year months only, up to selected month
		const getAllActiveMonths = () => {
			const filteredSettings = getAllActiveSettings();

			const uniqueMonths = [];
			const seen = new Set();
			filteredSettings.forEach((setting) => {
				const key = `${setting.year}-${setting.month}`;
				if (!seen.has(key)) {
					seen.add(key);
					uniqueMonths.push({ month: setting.month, year: setting.year });
				}
			});

			if (uniqueMonths.length === 0) return "";

			const formatMonth = (month, year) =>
				`${getMonthShortName(month)} ${year}`;
			const first = uniqueMonths[0];
			const last = uniqueMonths[uniqueMonths.length - 1];
			if (first.year === last.year && first.month === last.month) {
				return formatMonth(first.month, first.year);
			}
			return `${formatMonth(first.month, first.year)} - ${formatMonth(last.month, last.year)}`;
		};

		// Sort report rows by grade first, then by teacher name
		// Create pairs to maintain synchronization between monthly and cumulative arrays
		const pairedRows = reportRows.monthly.map((monthlyRow, index) => ({
			monthly: monthlyRow,
			cumulative: reportRows.cumulative[index]
		}));

		// Sort pairs by gradeName first, then by teacherName
		pairedRows.sort((a, b) => {
			// First compare by grade name
			const gradeComparison = a.monthly.gradeName.localeCompare(b.monthly.gradeName);
			if (gradeComparison !== 0) {
				return gradeComparison;
			}
			// If grades are the same, compare by teacher name
			return a.monthly.teacherName.localeCompare(b.monthly.teacherName);
		});

		// Separate back into monthly and cumulative arrays
		const sortedMonthlyRows = pairedRows.map(pair => pair.monthly);
		const sortedCumulativeRows = pairedRows.map(pair => pair.cumulative);

		return {
			school: school,
			setting: primarySetting,
			settings: settingsForPeriod,
			reportRows: {
				monthly: sortedMonthlyRows,
				cumulative: sortedCumulativeRows
			},
			schoolScore: schoolScore,
			currentScore: avgImplementationScore,
			schoolCategory: schoolCategory,
			cumulativeMonths: getAllActiveMonths(),
			totals: {
				totalTeachers,
				totalPreGameActivity,
				totalGameRules,
				totalVideosRequired,
				avgImplementationScore
			}
		};
	};

	const processReportData = (school, teachers, schoolGrades, teacherGrades, gameResults, contentProgress, setting, reportSettings) => {
		// Helper function to get videos required for a specific grade
		const getVideosRequiredForGrade = (gradeName, setting) => {
			// Find the grade in schoolGrades to get its parent_grade
			const schoolGrade = schoolGrades.find(sg => sg.grades.name === gradeName);
			const gradeParentGrade = schoolGrade ? schoolGrade.grades.parent_grade : null;
			
			
			const matchingSetting = reportSettings.find(s => 
				s.parent_grade === gradeParentGrade && 
				s.month === setting.month && 
				s.year === setting.year
			);
			
			const result = matchingSetting ? matchingSetting.number_of_videos_to_be_completed : setting.number_of_videos_to_be_completed;
			
			return result;
		};
		
		// Group teachers by their assigned grades
		const teacherGradeMap = {};
		teacherGrades.forEach(tg => {
			if (!teacherGradeMap[tg.profile_id]) {
				teacherGradeMap[tg.profile_id] = [];
			}
			teacherGradeMap[tg.profile_id].push(tg.grades);
		});

		// Group completed content progress by teacher and content type
		const completedContentByTeacher = {};
		
		contentProgress.forEach(cp => {
			if (!completedContentByTeacher[cp.teacher_id]) {
				completedContentByTeacher[cp.teacher_id] = {
					preGame: 0,
					gameRules: 0,
					total: 0
				};
			}

			// Categorize by content title first, then content type or category concept
			const contentType = cp.course_contents?.content_type || '';
			const categoryConcept = cp.course_categories?.concept || '';
			const contentTitle = cp.course_contents?.title || '';
			
			// Prioritize content_title for more specific categorization
			// Check Pre-Game first to avoid "Pre-Game Activity" being categorized as Game Play
			if (contentTitle.toLowerCase().includes('pre') || 
				contentTitle.toLowerCase().includes('introduction')) {
				completedContentByTeacher[cp.teacher_id].preGame++;
			} else if (contentTitle.toLowerCase().includes('game') || 
					   contentTitle.toLowerCase().includes('rule')) {
				completedContentByTeacher[cp.teacher_id].gameRules++;
			} else if (contentType.toLowerCase().includes('pre') || 
					   contentType.toLowerCase().includes('introduction') ||
					   categoryConcept.toLowerCase().includes('pre') ||
					   categoryConcept.toLowerCase().includes('introduction')) {
				completedContentByTeacher[cp.teacher_id].preGame++;
			} else if (contentType.toLowerCase().includes('game') || 
					   contentType.toLowerCase().includes('rule') ||
					   categoryConcept.toLowerCase().includes('game') ||
					   categoryConcept.toLowerCase().includes('rule')) {
				completedContentByTeacher[cp.teacher_id].gameRules++;
			} else {
				// Default categorization - fall back to preGame
				completedContentByTeacher[cp.teacher_id].preGame++;
			}
			
			completedContentByTeacher[cp.teacher_id].total++;
		});
		

		// Skip game results processing - we only use teacher_content_progress

		// Calculate scores and create report rows
		const reportRows = [];
		const teacherScores = {};

		teachers.forEach(teacher => {
			const teacherGradeList = teacherGradeMap[teacher.id] || [];
			const teacherCompletedContent = completedContentByTeacher[teacher.id] || { preGame: 0, gameRules: 0, total: 0 };
			
			
			// Use ONLY completedContent (teacher_content_progress) as per user requirement
			const dataToUse = teacherCompletedContent;
			
			// Calculate implementation score for this month based on completed content only
			const monthlyScore = dataToUse.total > 0 
				? Math.min(100, (dataToUse.total / setting.number_of_videos_to_be_completed) * 100)
				: 0;

			// For cumulative score, use the same data for now
			const cumulativeScore = monthlyScore;

			teacherGradeList.forEach(grade => {
				// Get videos required for this specific grade
				const videosRequiredForGrade = getVideosRequiredForGrade(grade.name, setting);
				
				// Recalculate implementation score based on grade-specific videos required
				const gradeMonthlyScore = dataToUse.total > 0 
					? Math.min(100, (dataToUse.total / videosRequiredForGrade) * 100)
					: 0;
				
				const gradeCumulativeScore = gradeMonthlyScore;

				reportRows.push({
					schoolName: school.name,
					gradeName: grade.name,
					teacherName: teacher.full_name,
					videosRequired: videosRequiredForGrade,
					monthly: {
						preGameActivity: dataToUse.preGame,
						gameRules: dataToUse.gameRules,
						implementationScore: gradeMonthlyScore
					},
					cumulative: {
						preGameActivity: dataToUse.preGame,
						gameRules: dataToUse.gameRules,
						totalImplementation: dataToUse.total,
						cumulativeScore: gradeCumulativeScore
					}
				});
			});

			// Track teacher scores for summary
			if (!teacherScores[teacher.id]) {
				teacherScores[teacher.id] = {
					name: teacher.full_name,
					monthlyScores: [],
					cumulativeScores: []
				};
			}
			teacherScores[teacher.id].monthlyScores.push(monthlyScore);
			teacherScores[teacher.id].cumulativeScores.push(cumulativeScore);
		});

		// Calculate school overall score
		const allScores = Object.values(teacherScores).flatMap(t => t.cumulativeScores);
		const schoolScore = allScores.length > 0 
			? allScores.reduce((sum, score) => sum + score, 0) / allScores.length
			: 0;

		// Determine school category
		const getSchoolCategory = (score) => {
			if (score >= 90) return { category: "Green (Excellent)", color: "success" };
			if (score >= 80) return { category: "Blue (Very Good)", color: "info" };
			if (score >= 60) return { category: "Yellow (Good)", color: "warning" };
			if (score >= 50) return { category: "Orange (Marginal)", color: "warning" };
			return { category: "Red (Unsatisfactory)", color: "error" };
		};

		const schoolCategory = getSchoolCategory(schoolScore);

		return {
			school: school,
			setting: setting,
			schoolScore: schoolScore,
			currentScore: schoolScore, // For backward compatibility
			schoolCategory: schoolCategory,
			reportRows: reportRows,
			teacherScores: Object.values(teacherScores).map(t => ({
				name: t.name,
				august: t.monthlyScores.length > 0 ? t.monthlyScores.reduce((sum, score) => sum + score, 0) / t.monthlyScores.length : 0,
				cumulative: t.cumulativeScores.length > 0 ? t.cumulativeScores.reduce((sum, score) => sum + score, 0) / t.cumulativeScores.length : 0
			}))
		};
	};

	const handlePrint = () => {
		window.print();
	};

		const handleDownload = async () => {
		const dataToDownload = modifiedReportData || reportData;
		if (!dataToDownload) {
			showSnackbar("No report data to download", "error");
			return;
		}

		try {
			// Find the report content element
			const reportElement = document.getElementById('report-content');
			if (!reportElement) {
				showSnackbar("Report content not found", "error");
				return;
			}

			// Show loading state
			showSnackbar("Generating PDF...", "info");

			// Force fixed width for consistent PDF layout (same as desktop) on all devices.
			// A4 width at 96dpi: 210mm ≈ 794px. Prevents mobile narrow layout in PDF.
			const A4_WIDTH_PX = 794;
			const originalMaxWidth = reportElement.style.maxWidth;
			const originalWidth = reportElement.style.width;
			const originalOverflow = reportElement.style.overflow;
			reportElement.style.width = `${A4_WIDTH_PX}px`;
			reportElement.style.maxWidth = `${A4_WIDTH_PX}px`;
			reportElement.style.overflow = 'visible';

			// Allow layout to reflow to the new width (needed for mobile)
			await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 100)));

			const restoreReportStyles = () => {
				reportElement.style.width = originalWidth;
				reportElement.style.maxWidth = originalMaxWidth;
				reportElement.style.overflow = originalOverflow;
			};

			// Hide checkboxes and checkbox column before generating PDF
			const checkboxes = reportElement.querySelectorAll('input[type="checkbox"]');
			const tables = reportElement.querySelectorAll('table');
			const hiddenElements = [];
			
			// Hide all checkboxes
			checkboxes.forEach((checkbox) => {
				hiddenElements.push({ element: checkbox, originalDisplay: checkbox.style.display });
				checkbox.style.display = 'none';
			});
			
			// Hide the checkbox column only where the first cell actually contains a checkbox
			tables.forEach((table) => {
				const hasCheckboxes = table.querySelectorAll('input[type="checkbox"]').length > 0;
				if (!hasCheckboxes) return;
				
				const rows = table.querySelectorAll('tr');
				rows.forEach((row) => {
					const firstCell = row.querySelector('td:first-child, th:first-child');
					if (!firstCell) return;
					if (firstCell.querySelector('input[type="checkbox"]')) {
						hiddenElements.push({ element: firstCell, originalDisplay: firstCell.style.display });
						firstCell.style.display = 'none';
					}
				});
			});

			// Create canvas from the report element at fixed width for consistent compact layout
			const canvas = await html2canvas(reportElement, {
				scale: 2, // Higher quality
				useCORS: true,
				allowTaint: true,
				backgroundColor: '#ffffff',
				scrollX: 0,
				scrollY: 0,
				width: reportElement.scrollWidth,
				height: reportElement.scrollHeight
			});

			// Restore report element width and overflow
			restoreReportStyles();

			// Restore checkboxes and first column after generating PDF
			hiddenElements.forEach(({ element, originalDisplay }) => {
				element.style.display = originalDisplay || '';
			});

			// Create PDF: scale to full width to minimize left/right margins; use multiple pages if tall
			const pdf = new jsPDF('p', 'mm', 'a4');
			const pdfWidth = pdf.internal.pageSize.getWidth();
			const pdfHeight = pdf.internal.pageSize.getHeight();
			const imgWidth = canvas.width;
			const imgHeight = canvas.height;

			const marginMm = 3;
			const contentWidth = pdfWidth - 2 * marginMm;
			const contentHeight = pdfHeight - 2 * marginMm;
			// Scale to full content width (removes wide left/right margins)
			const scale = contentWidth / imgWidth;
			const scaledHeightMm = imgHeight * scale;

			if (scaledHeightMm <= contentHeight) {
				const imgY = marginMm + (contentHeight - scaledHeightMm) / 2;
				const imgData = canvas.toDataURL('image/png');
				pdf.addImage(imgData, 'PNG', marginMm, imgY, contentWidth, scaledHeightMm);
			} else {
				// Multi-page: draw full-width slices per page
				const scalePxToMm = contentWidth / imgWidth;
				let sourceY = 0;
				let pageNum = 0;
				while (sourceY < imgHeight) {
					if (pageNum > 0) pdf.addPage();
					const remainingMm = scaledHeightMm - sourceY * scale;
					const sliceHeightMm = Math.min(contentHeight, remainingMm);
					const sliceHeightPx = Math.min(sliceHeightMm / scale, imgHeight - sourceY);
					const sliceCanvas = document.createElement('canvas');
					sliceCanvas.width = imgWidth;
					sliceCanvas.height = Math.ceil(sliceHeightPx);
					const ctx = sliceCanvas.getContext('2d');
					ctx.fillStyle = '#ffffff';
					ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
					ctx.drawImage(canvas, 0, sourceY, imgWidth, sliceHeightPx, 0, 0, imgWidth, sliceHeightPx);
					const sliceData = sliceCanvas.toDataURL('image/png');
					pdf.addImage(sliceData, 'PNG', marginMm, marginMm, contentWidth, sliceHeightMm);
					sourceY += sliceHeightPx;
					pageNum += 1;
				}
			}

			// Generate filename
			const schoolName = dataToDownload.school.name.replace(/[^a-zA-Z0-9]/g, '_');
			const monthYear = `${getMonthName(dataToDownload.setting.month)}_${dataToDownload.setting.year}`;
			const filename = `Implementation_Report_${schoolName}_${monthYear}${modifiedReportData ? '_Modified' : ''}.pdf`;

			// Download the PDF
			pdf.save(filename);
			showSnackbar("PDF downloaded successfully", "success");
		} catch (error) {
			console.error("Error generating PDF:", error);
			showSnackbar("Error generating PDF", "error");
			// Restore report element if we had set fixed width (e.g. on mobile)
			const reportElement = document.getElementById('report-content');
			if (reportElement && reportElement.style.width === '794px') {
				reportElement.style.width = '';
				reportElement.style.maxWidth = '';
				reportElement.style.overflow = '';
			}
		}
	};

	const handleDownloadExcel = () => {
		const dataToDownload = modifiedReportData || reportData;
		if (!dataToDownload) {
			showSnackbar("No report data to download", "error");
			return;
		}

		try {
			showSnackbar("Generating Excel...", "info");

			// Create a new workbook
			const workbook = XLSX.utils.book_new();

			// Prepare main report data
			const mainReportData = [];
			
			// Add header row
			mainReportData.push([
				"Grade Name",
				"Teacher Name",
				"Pre-Game Activity (Monthly)",
				"Game Play (Monthly)",
				"Implementation Score (Monthly) %",
				"Pre-Game Activity (Cumulative)",
				"Game Play (Cumulative)",
				"Total Implementation till date",
				"Cumulative Implementation Score %"
			]);

			// Add data rows
			dataToDownload.reportRows.monthly.forEach((monthlyRow, index) => {
				const cumulativeRow = dataToDownload.reportRows.cumulative[index];
				mainReportData.push([
					monthlyRow.gradeName,
					monthlyRow.teacherName,
					monthlyRow.preGameActivity,
					monthlyRow.gameRules,
					monthlyRow.implementationScore.toFixed(2),
					cumulativeRow.preGameActivity,
					cumulativeRow.gameRules,
					cumulativeRow.preGameActivity + cumulativeRow.gameRules,
					cumulativeRow.implementationScore.toFixed(2)
				]);
			});

			// Add empty row as separator
			mainReportData.push([]);
			
			// Add Teacher Score Summary section header
			mainReportData.push(["Teacher Score Summary"]);
			mainReportData.push([
				"Teacher Name",
				"Monthly Score %",
				"Cumulative Score %"
			]);

			// Calculate teacher scores
			const teacherScores = {};
			
			// Process monthly data
			dataToDownload.reportRows.monthly.forEach(row => {
				if (!teacherScores[row.teacherName]) {
					teacherScores[row.teacherName] = {
						name: row.teacherName,
						monthlyVideosRequired: 0,
						monthlyCompleted: 0,
						cumulativeVideosRequired: 0,
						cumulativeCompleted: 0
					};
				}
				teacherScores[row.teacherName].monthlyVideosRequired += row.videosRequired;
				teacherScores[row.teacherName].monthlyCompleted += (row.preGameActivity + row.gameRules);
			});

			// Process cumulative data
			dataToDownload.reportRows.cumulative.forEach(row => {
				if (teacherScores[row.teacherName]) {
					teacherScores[row.teacherName].cumulativeVideosRequired += row.videosRequired;
					teacherScores[row.teacherName].cumulativeCompleted += (row.preGameActivity + row.gameRules);
				}
			});

			// Add teacher summary rows
			Object.values(teacherScores).forEach(teacher => {
				const monthlyScore = teacher.monthlyVideosRequired > 0 
					? Math.min(100, (teacher.monthlyCompleted / teacher.monthlyVideosRequired) * 100)
					: 0;
				
				const cumulativeScore = teacher.cumulativeVideosRequired > 0 
					? Math.min(100, (teacher.cumulativeCompleted / teacher.cumulativeVideosRequired) * 100)
					: 0;

				mainReportData.push([
					teacher.name,
					monthlyScore.toFixed(2),
					cumulativeScore.toFixed(2)
				]);
			});

			// Create single worksheet with all data
			const mainWorksheet = XLSX.utils.aoa_to_sheet(mainReportData);
			
			// Set column widths (using max width needed for both sections)
			mainWorksheet['!cols'] = [
				{ wch: 15 }, // Grade Name
				{ wch: 25 }, // Teacher Name (main report) / Monthly Score
				{ wch: 25 }, // Pre-Game Activity (Monthly) / Cumulative Score
				{ wch: 20 }, // Game Play (Monthly)
				{ wch: 30 }, // Implementation Score (Monthly)
				{ wch: 25 }, // Pre-Game Activity (Cumulative)
				{ wch: 20 }, // Game Play (Cumulative)
				{ wch: 30 }, // Total Implementation till date
				{ wch: 35 }  // Cumulative Implementation Score
			];

			// Add worksheet to workbook
			XLSX.utils.book_append_sheet(workbook, mainWorksheet, "Implementation Report");

			// Generate filename
			const schoolName = dataToDownload.school.name.replace(/[^a-zA-Z0-9]/g, '_');
			const monthYear = `${getMonthName(dataToDownload.setting.month)}_${dataToDownload.setting.year}`;
			const filename = `Implementation_Report_${schoolName}_${monthYear}${modifiedReportData ? '_Modified' : ''}.xlsx`;

			// Write and download the file
			XLSX.writeFile(workbook, filename);
			showSnackbar("Excel file downloaded successfully", "success");
		} catch (error) {
			console.error("Error generating Excel:", error);
			showSnackbar("Error generating Excel", "error");
		}
	};

	const getMonthName = (month) => {
		const months = [
			"January", "February", "March", "April", "May", "June",
			"July", "August", "September", "October", "November", "December"
		];
		return months[month - 1] || month;
	};

	const getMonthShortName = (month) => {
		const months = [
			"Jan", "Feb", "Mar", "Apr", "May", "Jun",
			"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
		];
		return months[month - 1] || month;
	};

	const getScoreColor = (score) => {
		if (score >= 90) return { bg: '#e8f5e8', border: '#4caf50', text: '#000000' }; // Green
		if (score >= 80) return { bg: '#e3f2fd', border: '#2196f3', text: '#000000' }; // Blue
		if (score >= 60) return { bg: '#fffde7', border: '#ffeb3b', text: '#000000' }; // Yellow
		if (score >= 50) return { bg: '#fff3e0', border: '#ff9800', text: '#000000' }; // Orange
		return { bg: '#ffebee', border: '#f44336', text: '#000000' }; // Red
	};

	const getDistinctPeriods = () => {
		const periods = {};
		reportSettings.forEach((setting) => {
			if (!monthYearInAcademicYear(setting.month, setting.year, selectedAcademicYear)) {
				return;
			}

			const key = `${setting.month}/${setting.year}`;
			if (!periods[key]) {
				periods[key] = {
					month: setting.month,
					year: setting.year,
					label: `${getMonthName(setting.month)} ${setting.year}`,
				};
			}
		});
		return Object.values(periods).sort((a, b) => {
			if (a.year !== b.year) return b.year - a.year;
			return b.month - a.month;
		});
	};

	const handlePeriodChange = (periodKey) => {
		setSelectedPeriod(periodKey);
	};

	const persistModifications = (excluded, grouped, monthlyRows) => {
		if (!selectedSchool || !selectedAcademicYear || !monthlyRows) return;
		saveReportModifications(
			selectedSchool,
			selectedAcademicYear,
			indicesToModifications(monthlyRows, excluded, grouped)
		);
	};

	const applyModificationsToDisplay = (excluded, grouped) => {
		if (!reportData) return;
		if (excluded.size > 0 || Object.keys(grouped).length > 0) {
			setModifiedReportData(
				applyReportModifications(reportData, excluded, grouped)
			);
		} else {
			setModifiedReportData(null);
		}
	};

	// Handle row selection
	const toggleDisplayRowSelection = (sourceIndices) => {
		if (!sourceIndices.length) return;
		const newSelected = new Set(selectedRows);
		const allSelected = sourceIndices.every((i) => newSelected.has(i));

		if (allSelected) {
			sourceIndices.forEach((i) => newSelected.delete(i));
		} else {
			sourceIndices.forEach((i) => newSelected.add(i));
		}
		setSelectedRows(newSelected);
	};

	const isDisplayRowSelected = (sourceIndices) =>
		sourceIndices.length > 0 &&
		sourceIndices.every((i) => selectedRows.has(i));

	// Handle select all
	const toggleSelectAll = () => {
		if (!reportData) return;
		const selectableIndices = reportData.reportRows.monthly
			.map((_, index) => index)
			.filter((index) => !excludedRows.has(index));
		const allSelected =
			selectableIndices.length > 0 &&
			selectableIndices.every((index) => selectedRows.has(index));

		if (allSelected) {
			setSelectedRows(new Set());
		} else {
			setSelectedRows(new Set(selectableIndices));
		}
	};

	const selectableRowCount = reportData
		? reportData.reportRows.monthly.length - excludedRows.size
		: 0;
	const allSelectableSelected =
		selectableRowCount > 0 && selectedRows.size === selectableRowCount;

	// Exclude selected rows
	const handleExcludeRows = () => {
		if (selectedRows.size === 0) {
			showSnackbar("Please select rows to exclude", "warning");
			return;
		}
		const newExcluded = new Set([...excludedRows, ...selectedRows]);

		const newGrouped = { ...groupedRows };
		Object.keys(newGrouped).forEach((groupId) => {
			const filtered = newGrouped[groupId].filter(
				(idx) => !selectedRows.has(idx)
			);
			if (filtered.length >= 2) {
				newGrouped[groupId] = filtered;
			} else {
				delete newGrouped[groupId];
			}
		});

		const excludedCount = selectedRows.size;
		setExcludedRows(newExcluded);
		setGroupedRows(newGrouped);
		setSelectedRows(new Set());
		persistModifications(
			newExcluded,
			newGrouped,
			reportData.reportRows.monthly
		);
		applyModificationsToDisplay(newExcluded, newGrouped);
		showSnackbar(`${excludedCount} row(s) excluded`, "success");
	};

	// Group selected rows
	const handleGroupRows = () => {
		if (selectedRows.size < 2) {
			showSnackbar("Please select at least 2 rows to group", "warning");
			return;
		}

		const newGrouped = { ...groupedRows };
		Object.keys(newGrouped).forEach((groupId) => {
			const filtered = newGrouped[groupId].filter(
				(idx) => !selectedRows.has(idx)
			);
			if (filtered.length >= 2) {
				newGrouped[groupId] = filtered;
			} else {
				delete newGrouped[groupId];
			}
		});

		const groupId = `group_${nextGroupId}`;
		const groupedCount = selectedRows.size;
		newGrouped[groupId] = Array.from(selectedRows);
		setGroupedRows(newGrouped);
		setNextGroupId(nextGroupId + 1);
		setSelectedRows(new Set());
		persistModifications(
			excludedRows,
			newGrouped,
			reportData.reportRows.monthly
		);
		applyModificationsToDisplay(excludedRows, newGrouped);
		showSnackbar(`${groupedCount} row(s) grouped`, "success");
	};

	// Remove exclusion
	const handleRemoveExclusion = (index) => {
		const newExcluded = new Set(excludedRows);
		newExcluded.delete(index);
		setExcludedRows(newExcluded);
		persistModifications(
			newExcluded,
			groupedRows,
			reportData.reportRows.monthly
		);
		applyModificationsToDisplay(newExcluded, groupedRows);
	};

	// Remove grouping
	const handleUngroup = (groupId) => {
		const newGrouped = { ...groupedRows };
		delete newGrouped[groupId];
		setGroupedRows(newGrouped);
		persistModifications(
			excludedRows,
			newGrouped,
			reportData.reportRows.monthly
		);
		applyModificationsToDisplay(excludedRows, newGrouped);
	};

	// Regenerate report with modifications
	const regenerateReport = () => {
		if (!reportData) {
			showSnackbar("No report data available", "error");
			return;
		}

		const modifiedData = applyReportModifications(
			reportData,
			excludedRows,
			groupedRows
		);
		setModifiedReportData(modifiedData);
		showSnackbar("Report regenerated successfully", "success");
	};

	// Get the data to display (modified or original)
	const displayData = modifiedReportData || reportData;


	return (
		<Container maxWidth="xl" sx={{ py: 4 }}>
			<Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
				Implementation Status Report Generation
			</Typography>
			<Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
				Generate implementation status reports for schools based on uploaded videos and activities.
			</Typography>

			{/* Controls */}
			<Card sx={{ mb: 4 }}>
				<CardContent>
					<Grid container spacing={3} alignItems="center">
						<Grid item xs={12} md={3}>
							<Autocomplete
								options={schools}
								getOptionLabel={(option) => option.name}
								value={schools.find(school => school.id === selectedSchool) || null}
								onChange={(event, newValue) => {
									setSelectedSchool(newValue ? newValue.id : "");
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Select School"
										size="small"
										placeholder="Search for a school..."
									/>
								)}
								renderOption={(props, option) => (
									<li {...props} key={option.id}>
										{option.name}
									</li>
								)}
								noOptionsText="No schools found"
								clearOnEscape
								selectOnFocus
								handleHomeEndKeys
							/>
						</Grid>
						<Grid item xs={12} md={2}>
							<AcademicYearSelector
								value={selectedAcademicYear}
								onChange={setSelectedAcademicYear}
								sx={{ width: "100%" }}
								minWidth={undefined}
							/>
						</Grid>
						<Grid item xs={12} md={3}>
							<FormControl fullWidth size="small">
								<InputLabel>Report Period</InputLabel>
								<Select
									value={selectedPeriod}
									onChange={(e) => handlePeriodChange(e.target.value)}
									label="Report Period"
								>
									{getDistinctPeriods().map((period) => (
										<MenuItem key={`${period.month}/${period.year}`} value={`${period.month}/${period.year}`}>
											{period.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12} md={4}>
							<Stack direction="row" spacing={2}>
								<Button
									variant="contained"
									onClick={generateReport}
									disabled={generating || !selectedSchool || !selectedPeriod}
									startIcon={generating ? <CircularProgress size={16} /> : <RefreshIcon />}
								>
									{generating ? "Generating..." : "Generate Report"}
								</Button>
								{reportData && (
									<>
										<Button
											variant="outlined"
											startIcon={<DownloadIcon />}
											onClick={handleDownload}
										>
                                            PDF
										</Button>
										<Button
											variant="outlined"
											startIcon={<DownloadIcon />}
											onClick={handleDownloadExcel}
											sx={{ ml: 1 }}
										>
                                            Excel
										</Button>
									</>
								)}
							</Stack>
						</Grid>
					</Grid>
				</CardContent>
			</Card>

			{/* Report Display */}
			{reportData && (
				<>
					{/* Action Controls for Row Modifications */}
					<Card sx={{ mb: 2 }}>
						<CardContent>
							<Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
								<Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 2 }}>
									Row Actions:
								</Typography>
								<Button
									size="small"
									variant="outlined"
									startIcon={<CheckBoxIcon />}
									onClick={toggleSelectAll}
									disabled={selectableRowCount === 0}
								>
									{allSelectableSelected ? "Deselect All" : "Select All"}
								</Button>
								<Button
									size="small"
									variant="outlined"
									color="error"
									startIcon={<DeleteIcon />}
									onClick={handleExcludeRows}
									disabled={selectedRows.size === 0}
								>
									Exclude Selected ({selectedRows.size})
								</Button>
								<Button
									size="small"
									variant="outlined"
									color="primary"
									startIcon={<GroupIcon />}
									onClick={handleGroupRows}
									disabled={selectedRows.size < 2}
								>
									Group Selected ({selectedRows.size})
								</Button>
								<Button
									size="small"
									variant="contained"
									color="primary"
									startIcon={<RefreshIcon />}
									onClick={regenerateReport}
									disabled={excludedRows.size === 0 && Object.keys(groupedRows).length === 0}
								>
									Regenerate Report
								</Button>
								{(excludedRows.size > 0 || Object.keys(groupedRows).length > 0) && (
									<Chip
										label={`${excludedRows.size} excluded, ${Object.keys(groupedRows).length} groups`}
										size="small"
										color="info"
										variant="outlined"
									/>
								)}
							</Stack>
						</CardContent>
					</Card>
					<Paper id="report-content" sx={{ p: 3 }}>
					

					{/* Report Header with School Info and Performance Circles */}
					<Box sx={{ mb: 4 }}>
						{/* Top row - School Info and Performance Circles */}
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
							{/* Left side - School Info */}
							<Box>
							<img 
							src="/logo.png" 
							alt="Funmiyo Logo" 
							style={{ 
								height: '40px', 
								width: 'auto',
							}} 
						/>
								<Typography variant="h6" sx={{ fontWeight: 500, color: '#666' }}>
									Implementation Status Report - {getMonthName(displayData.setting.month)} {displayData.setting.year}
								</Typography>
								<Typography variant="h5" sx={{ fontWeight: 500, color: '#000' }}>
									{displayData.school.name}
								</Typography>
								{modifiedReportData && (
									<Chip
										label="Modified Report"
										size="small"
										color="warning"
										variant="outlined"
										sx={{ mt: 1 }}
									/>
								)}
							</Box>

							{/* Right side - Performance Circle */}
							<Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
								{/* Cumulative Performance */}
								<Box sx={{ textAlign: 'center' }}>
									{(() => {
										const cumulativeScore = displayData.schoolScore || 0;
										const colors = getScoreColor(cumulativeScore);
										return (
											<Box sx={{ 
												width: 80, 
												height: 80, 
												borderRadius: '50%', 
												display: 'flex', 
												alignItems: 'center', 
												justifyContent: 'center',
												backgroundColor: colors.bg,
												border: `3px solid ${colors.border}`,
												mb: 1
											}}>
												<Typography variant="h6" sx={{ fontWeight: 600, color: colors.text }}>
													{cumulativeScore.toFixed(0)}%
												</Typography>
											</Box>
										);
									})()}
									<Typography variant="caption" sx={{ fontWeight: 500, color: '#666' }}>
										Cumulative
									</Typography>
								</Box>
							</Box>
						</Box>

					</Box>

					{/* Main Data Table */}
					<TableContainer sx={{ mb: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
						<Table size="small" sx={{
							'& .MuiTableCell-root': {
								border: '1px solid #e0e0e0',
								padding: '4px 6px',
								fontSize: '0.75rem',
								lineHeight: 1.2,
							},
							'& .MuiTableCell-head': {
								backgroundColor: '#f5f5f5',
								fontWeight: 600,
								border: '1px solid #e0e0e0',
								padding: '6px 8px',
								fontSize: '0.8rem',
							},
							'& .MuiTableRow-root:hover': {
								backgroundColor: '#f9f9f9',
							},
						}}>
							<TableHead>
								<TableRow>
									<TableCell rowSpan={2} sx={{ fontWeight: 600, verticalAlign: 'middle', width: '50px' }}>
										<Checkbox
											checked={allSelectableSelected}
											indeterminate={selectedRows.size > 0 && !allSelectableSelected}
											onChange={toggleSelectAll}
											size="small"
											disabled={selectableRowCount === 0}
										/>
									</TableCell>
									<TableCell rowSpan={2} sx={{ fontWeight: 600, verticalAlign: 'middle' }}>Grade Name</TableCell>
									<TableCell rowSpan={2} sx={{ fontWeight: 600, verticalAlign: 'middle' }}>Teacher Name</TableCell>
									<TableCell colSpan={3} sx={{ fontWeight: 600, textAlign: "center" }}>
										Implementation Status - {getMonthName(displayData.setting.month)} {displayData.setting.year}
									</TableCell>
									<TableCell colSpan={4} sx={{ fontWeight: 600, textAlign: "center" }}>
										Cumulative Report ({displayData.cumulativeMonths})
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Pre-Game Activity</TableCell>
									<TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Game Play</TableCell>
									<TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Implementation Score</TableCell>
									<TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Pre-Game Activity</TableCell>
									<TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Game Play</TableCell>
									<TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Total Implementation till date</TableCell>
									<TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Cumulative Implementation Score</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{displayData.reportRows.monthly.map((monthlyRow, displayIndex) => {
									const cumulativeRow = displayData.reportRows.cumulative[displayIndex];
									const cumulativeScore = cumulativeRow.implementationScore;
									const shouldHighlight = cumulativeScore < 50;
									const sourceIndices = getSourceIndicesForDisplayRow(
										monthlyRow,
										reportData.reportRows,
										excludedRows,
										groupedRows
									);
									const isGroupedRow = sourceIndices.length > 1;

									return (
										<TableRow 
											key={displayIndex}
											sx={{
												backgroundColor: shouldHighlight ? '#ffebee' : isGroupedRow ? '#e3f2fd' : 'inherit',
												'&:hover': {
													backgroundColor: shouldHighlight ? '#ffcdd2' : isGroupedRow ? '#bbdefb' : '#f5f5f5'
												}
											}}
										>
											<TableCell>
												<Checkbox
													checked={isDisplayRowSelected(sourceIndices)}
													onChange={() => toggleDisplayRowSelection(sourceIndices)}
													size="small"
													disabled={sourceIndices.length === 0}
												/>
											</TableCell>
											<TableCell>{monthlyRow.gradeName}</TableCell>
											<TableCell>{monthlyRow.teacherName}</TableCell>
											<TableCell align="center">{monthlyRow.preGameActivity}</TableCell>
											<TableCell align="center">{monthlyRow.gameRules}</TableCell>
											<TableCell align="center">{monthlyRow.implementationScore.toFixed(2)}%</TableCell>
											<TableCell align="center">{cumulativeRow.preGameActivity}</TableCell>
											<TableCell align="center">{cumulativeRow.gameRules}</TableCell>
											<TableCell align="center">{cumulativeRow.preGameActivity + cumulativeRow.gameRules}</TableCell>
											<TableCell align="center">{cumulativeRow.implementationScore.toFixed(2)}%</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>

					{/* Teacher Score Summary */}
					<Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
						Teacher Score Summary
					</Typography>
					
					{/* Two Column Layout */}
					<Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
						{/* Left Column - Teacher Summary Table */}
						<Box sx={{ flex: '1 1 50%', display: 'flex', justifyContent: 'flex-start' }}>
							<TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 1, width: 'fit-content' }}>
								<Table size="small" sx={{
									width: 'auto',
									'& .MuiTableCell-root': {
										border: '1px solid #e0e0e0',
										padding: '4px 6px',
										fontSize: '0.75rem',
										lineHeight: 1.2,
									},
									'& .MuiTableCell-head': {
										backgroundColor: '#f5f5f5',
										fontWeight: 600,
										border: '1px solid #e0e0e0',
										padding: '6px 8px',
										fontSize: '0.8rem',
									},
									'& .MuiTableRow-root:hover': {
										backgroundColor: '#f9f9f9',
									},
								}}>
									<TableHead>
										<TableRow>
											<TableCell sx={{ fontWeight: 600 }}>Teacher</TableCell>
											<TableCell sx={{ fontWeight: 600, textAlign: "center" }}>{getMonthName(displayData.setting.month)} {displayData.setting.year}</TableCell>
											<TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Cumulative</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{(() => {
											// Group rows by teacher and calculate consolidated scores for monthly and cumulative
											const teacherScores = {};
											
											// Process monthly data
											displayData.reportRows.monthly.forEach(row => {
												if (!teacherScores[row.teacherName]) {
													teacherScores[row.teacherName] = {
														name: row.teacherName,
														monthlyVideosRequired: 0,
														monthlyCompleted: 0,
														cumulativeVideosRequired: 0,
														cumulativeCompleted: 0,
														grades: []
													};
												}
												teacherScores[row.teacherName].monthlyVideosRequired += row.videosRequired;
												teacherScores[row.teacherName].monthlyCompleted += (row.preGameActivity + row.gameRules);
												teacherScores[row.teacherName].grades.push(row.gradeName);
											});

											// Process cumulative data
											displayData.reportRows.cumulative.forEach(row => {
												if (teacherScores[row.teacherName]) {
													teacherScores[row.teacherName].cumulativeVideosRequired += row.videosRequired;
													teacherScores[row.teacherName].cumulativeCompleted += (row.preGameActivity + row.gameRules);
												}
											});

											// Calculate consolidated scores for each teacher
											return Object.values(teacherScores).map((teacher, index) => {
												const monthlyScore = teacher.monthlyVideosRequired > 0 
													? Math.min(100, (teacher.monthlyCompleted / teacher.monthlyVideosRequired) * 100)
													: 0;
												
												const cumulativeScore = teacher.cumulativeVideosRequired > 0 
													? Math.min(100, (teacher.cumulativeCompleted / teacher.cumulativeVideosRequired) * 100)
													: 0;
												
												const shouldHighlight = cumulativeScore < 50;
												
												return (
													<TableRow 
														key={index}
														sx={{
															backgroundColor: shouldHighlight ? '#ffebee' : 'inherit',
															'&:hover': {
																backgroundColor: shouldHighlight ? '#ffcdd2' : '#f5f5f5'
															}
														}}
													>
														<TableCell>{teacher.name}</TableCell>
														<TableCell align="center">{monthlyScore.toFixed(0)}%</TableCell>
														<TableCell align="center">{cumulativeScore.toFixed(0)}%</TableCell>
													</TableRow>
												);
											});
										})()}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>

						{/* Right Column - Note and Scoring Legend */}
						<Box sx={{ flex: '1 1 50%' }}>
							{/* Note Section */}
							<Box sx={{ mb: 3 }}>
								<Typography variant="caption" sx={{ fontStyle: "italic", lineHeight: 1.4 }}>
									<strong>Note:</strong> To achieve a 100% implementation score by {getMonthName(displayData.setting.month)} {displayData.setting.year}, 
									Funmiyo expected each teacher to complete the {(() => {
										// Use the same calculation as ReportSettings: get max per month, then sum
										// Only use settings with status: true (already filtered in fetchReportSettings)
										const monthlyTotals = {};
										
										const filteredSettings = filterReportSettingsForCumulative(
											reportSettings,
											displayData.setting.month,
											displayData.setting.year,
											selectedAcademicYear
										).sort((a, b) => {
											if (a.year !== b.year) return a.year - b.year;
											return a.month - b.month;
										});

										// For each month, get the maximum videos required (same logic as ReportSettings)
										filteredSettings.forEach((setting) => {
											const key = `${setting.year}-${setting.month}`;
											const videos = setting.number_of_videos_to_be_completed || 0;
											
											if (!monthlyTotals[key]) {
												monthlyTotals[key] = {
													month: setting.month,
													year: setting.year,
													totalVideos: videos,
												};
											} else {
												// Keep the higher value (Math.max like in ReportSettings)
												monthlyTotals[key].totalVideos = Math.max(monthlyTotals[key].totalVideos, videos);
											}
										});

										// Sum all monthly maximums (same as ReportSettings total row)
										const totalVideos = Object.values(monthlyTotals).reduce((sum, total) => sum + total.totalVideos, 0);
										return totalVideos;
									})()} Funmath classes 
									and upload the corresponding implementation videos for all the grades they are handling.
								</Typography>
							</Box>

							{/* Scoring Legend */}
							<Box>
								<Typography variant="caption" sx={{ mb: 1, fontWeight: 600, display: 'block' }}>
									Scoring Legend:
								</Typography>
								<Stack direction="row" spacing={0.5} flexWrap="wrap">
									<Chip 
										label="Red (0-49)" 
										size="small" 
										variant="outlined" 
										sx={{ 
											fontSize: '0.7rem', 
											height: '20px',
											backgroundColor: '#ffebee',
											borderColor: '#f44336',
											color: '#d32f2f'
										}} 
									/>
									<Chip 
										label="Orange (50-59)" 
										size="small" 
										variant="outlined" 
										sx={{ 
											fontSize: '0.7rem', 
											height: '20px',
											backgroundColor: '#fff3e0',
											borderColor: '#ff9800',
											color: '#ef6c00'
										}} 
									/>
									<Chip 
										label="Yellow (60-79)" 
										size="small" 
										variant="outlined" 
										sx={{ 
											fontSize: '0.7rem', 
											height: '20px',
											backgroundColor: '#fffde7',
											borderColor: '#ffeb3b',
											color: '#f57f17'
										}} 
									/>
									<Chip 
										label="Blue (80-89)" 
										size="small" 
										variant="outlined" 
										sx={{ 
											fontSize: '0.7rem', 
											height: '20px',
											backgroundColor: '#e3f2fd',
											borderColor: '#2196f3',
											color: '#1976d2'
										}} 
									/>
									<Chip 
										label="Green (90+)" 
										size="small" 
										variant="outlined" 
										sx={{ 
											fontSize: '0.7rem', 
											height: '20px',
											backgroundColor: '#e8f5e8',
											borderColor: '#4caf50',
											color: '#2e7d32'
										}} 
									/>
								</Stack>
							</Box>
						</Box>
							</Box>
						</Paper>
				</>
			)}

			{/* Snackbar for notifications */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			>
				<Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default ReportGeneration;
